import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import auth0Config from './config/auth0';
import Header from './components/Header';
import LeftDrawer from './components/LeftDrawer';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './pages/Dashboard';
import SharedPrompts from './pages/SharedPrompts';
import Search from './pages/Search';
import ProjectDetail from './pages/ProjectDetail';
import PromptDetail from './pages/PromptDetail';
import PromptDiff from './pages/PromptDiff';
import TestSets from './pages/TestSets';
import TestSetsPage from './pages/TestSetsPage';
import TestSetDetail from './pages/TestSetDetail';
import TestEvaluation from './pages/TestEvaluation';
import Logs from './pages/Logs';
import PromptLogs from './pages/PromptLogs';
import EvaluationDetail from './pages/EvaluationDetail';
import ApiKeys from './pages/ApiKeys';
import PromptSettings from './pages/PromptSettings';
import ProjectSettings from './pages/ProjectSettings';
import UserSettings from './pages/UserSettings';
import Login from './pages/Login';
import Register from './pages/Register';
// Beta pages removed - application is no longer in beta

import Notifications from './pages/Notifications';

// MVP Feature 6: Git-like Collaboration Components
import BranchDetail from './pages/BranchDetail';
import PromptPullRequests from './pages/PromptPullRequests';
import PullRequestDetail from './pages/PullRequestDetail';
import PromptCollaborators from './pages/PromptCollaborators';
import PromptForks from './pages/PromptForks';

// MVP Feature 8: Experiments Components
import PromptExperiments from './pages/PromptExperiments';
import ExperimentDetail from './pages/ExperimentDetail';

// Composer Components
import ComposerFlowsList from './pages/ComposerFlowsList';
import Composer from './pages/Composer';
import FlowResults from './pages/FlowResults';


import { getCurrentUser } from './services/api';
import './index.css';
import { ApiKeysProvider } from './contexts/ApiKeysContext';
import { FeedbackProvider, useFeedback } from './contexts/FeedbackContext';
import ContextualFeedback from './components/ContextualFeedback';
import FloatingBugReport from './components/FloatingBugReport';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// OAuth Callback Handler component
const OAuthCallback = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleAuth0Callback = () => {
      const checkAuth = () => {
        if (!loading) {
          if (user) {
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/login', { replace: true });
          }
          setIsProcessing(false);
        }
      };

      checkAuth();
      
      if (loading) {
        const timer = setTimeout(() => {
          checkAuth();
        }, 5000); // Wait up to 5 seconds for auth to complete
        
        return () => clearTimeout(timer);
      }
    };

    handleAuth0Callback();
  }, [user, loading, navigate]);

  if (isProcessing || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-celadon-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-white shadow-soft flex items-center justify-center mx-auto mb-6">
            <img src="/banyan-icon.jpeg" alt="Banyan" className="w-8 h-8" />
          </div>
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium text-lg">Completing sign in...</p>
          <p className="text-gray-500 text-sm mt-1">Please wait while we authenticate you with Google</p>
        </div>
      </div>
    );
  }

  return null;
};

// Dashboard Route Handler - Auth0 handles redirects automatically
const DashboardRoute = ({ user }) => {
  return <ProtectedRoute><Dashboard user={user} /></ProtectedRoute>;
};

// Main App Content component
const AppContent = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [notificationRefresh, setNotificationRefresh] = useState(null);
  const [isDrawerPinned, setIsDrawerPinned] = useState(false);
  const { user: authUser, loading: authLoading } = useAuth();
  const { contextualFeedback, hideContextualFeedback } = useFeedback();

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!authUser) {
        setUserProfile(null);
        setProfileLoading(false);
        return;
      }

      // Check if we already have user profile data to avoid redundant calls
      if (userProfile && userProfile.id === authUser.id) {
        setProfileLoading(false);
        return;
      }

      try {
        // Get user profile from backend
        const userData = await getCurrentUser();
        setUserProfile(userData);
      } catch (error) {
        console.error('Failed to load user profile:', error);
        // Use Supabase user data as fallback
        setUserProfile({
          id: authUser.id,
          email: authUser.email,
          full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
          avatar_url: authUser.user_metadata?.avatar_url || null,
        });
      } finally {
        setProfileLoading(false);
      }
    };

    if (!authLoading) {
      loadUserProfile();
    }
  }, [authUser, authLoading]);

  // Show loading spinner while auth or profile is loading
  if (authLoading || (authUser && profileLoading)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-celadon-50/30 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 shadow-soft flex items-center justify-center mx-auto mb-6">
            <img src="/banyan-icon.jpeg" alt="Banyan" className="w-8 h-8" />
          </div>
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-700 dark:text-gray-200 font-medium text-lg">Loading Banyan...</p>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Setting up your intelligent workspace</p>
        </div>
      </div>
    );
  }

  // Use userProfile if available, otherwise use authUser as fallback
  const currentUser = userProfile || (authUser ? {
    id: authUser.id,
    email: authUser.email,
    full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
  } : null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-celadon-50/30 dark:from-gray-900 dark:to-gray-800">
      <Header 
        user={currentUser} 
        onNotificationUpdate={setNotificationRefresh}
      />
      
      {/* Left Drawer - only show when user is authenticated */}
      {currentUser && (
        <LeftDrawer 
          user={currentUser} 
          onPinChange={setIsDrawerPinned}
        />
      )}
      
      <main className={isDrawerPinned ? 'ml-80 transition-all duration-300' : 'transition-all duration-300'}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/callback" element={<OAuthCallback />} />
          
          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><Dashboard user={currentUser} /></ProtectedRoute>} />
          <Route path="/dashboard" element={<DashboardRoute user={currentUser} />} />
          
          {/* Search route */}
          <Route path="/search" element={
            <div className={`${isDrawerPinned ? 'ml-80' : ''} transition-all duration-300`}>
              <ProtectedRoute><Search user={currentUser} /></ProtectedRoute>
            </div>
          } />
          
          {/* Other routes with container */}
          <Route path="/shared-prompts" element={
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isDrawerPinned ? 'pl-8' : ''}`}>
              <ProtectedRoute><SharedPrompts user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/projects/:projectId" element={
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isDrawerPinned ? 'pl-8' : ''}`}>
              <ProtectedRoute><ProjectDetail user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/projects/:projectId/test-sets" element={
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isDrawerPinned ? 'pl-8' : ''}`}>
              <ProtectedRoute><TestSets /></ProtectedRoute>
            </div>
          } />
          <Route path="/projects/:projectId/test-sets/:testSetId" element={
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isDrawerPinned ? 'pl-8' : ''}`}>
              <ProtectedRoute><TestSetDetail /></ProtectedRoute>
            </div>
          } />
          <Route path="/projects/:projectId/test-sets/:testSetId/evaluate/:promptVersionId" element={
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isDrawerPinned ? 'pl-8' : ''}`}>
              <ProtectedRoute><TestEvaluation /></ProtectedRoute>
            </div>
          } />
          <Route path="/prompts/:promptId" element={
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isDrawerPinned ? 'pl-8' : ''}`}>
              <ProtectedRoute><PromptDetail user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/prompts/:promptId/logs" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><PromptLogs user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/prompts/:promptId/logs/:logId/evaluation" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><EvaluationDetail user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/prompts/:promptId/diff/:version1/:version2" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><PromptDiff /></ProtectedRoute>
            </div>
          } />
          
          {/* MVP Feature 6: Git-like Collaboration Routes */}
          <Route path="/prompts/:promptId/branches/:branchId" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><BranchDetail user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/prompts/:promptId/pull-requests" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><PromptPullRequests user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/prompts/:promptId/pull-requests/:prId" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><PullRequestDetail user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/prompts/:promptId/collaborators" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><PromptCollaborators user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/prompts/:promptId/forks" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><PromptForks user={currentUser} /></ProtectedRoute>
            </div>
          } />
          
          {/* MVP Feature 8: Experiments Routes */}
          <Route path="/prompts/:promptId/experiments" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><PromptExperiments user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/experiments/:experimentId" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><ExperimentDetail user={currentUser} /></ProtectedRoute>
            </div>
          } />
          
          {/* Composer Routes */}
          <Route path="/projects/:projectId/composer" element={
            <ProtectedRoute><ComposerFlowsList user={currentUser} /></ProtectedRoute>
          } />
          <Route path="/projects/:projectId/composer/:flowId" element={
            <ProtectedRoute><Composer user={currentUser} /></ProtectedRoute>
          } />
          <Route path="/projects/:projectId/composer/:flowId/results/:executionId" element={
            <ProtectedRoute><FlowResults user={currentUser} /></ProtectedRoute>
          } />
          
          <Route path="/logs" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><Logs /></ProtectedRoute>
            </div>
          } />
          <Route path="/projects/:projectId/logs" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><Logs /></ProtectedRoute>
            </div>
          } />
          <Route path="/api-keys" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><ApiKeys /></ProtectedRoute>
            </div>
          } />
          <Route path="/notifications" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute>
                <Notifications onNotificationCountChange={notificationRefresh} />
              </ProtectedRoute>
            </div>
          } />
          <Route path="/settings" element={
            <ProtectedRoute><UserSettings /></ProtectedRoute>
          } />
          {/* Beta admin route removed - application is no longer in beta */}

          <Route path="/prompts/:promptId/settings" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><PromptSettings user={currentUser} /></ProtectedRoute>
            </div>
          } />
          <Route path="/projects/:projectId/settings" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><ProjectSettings /></ProtectedRoute>
            </div>
          } />
          <Route path="/test-sets" element={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ProtectedRoute><TestSetsPage /></ProtectedRoute>
            </div>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Contextual Feedback Component */}
      <ContextualFeedback
        isOpen={contextualFeedback.isOpen}
        onClose={hideContextualFeedback}
        actionName={contextualFeedback.actionName}
        actionContext={contextualFeedback.actionContext}
        position={contextualFeedback.position}
        autoClose={contextualFeedback.autoClose}
      />

      {/* Floating Bug Report Button - only show when user is authenticated */}
      {currentUser && <FloatingBugReport />}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={auth0Config.authorizationParams}
        useRefreshTokens={auth0Config.useRefreshTokens}
        cacheLocation={auth0Config.cacheLocation}
      >
        <ThemeProvider>
          <ErrorBoundary>
            <AuthProvider>
              <ApiKeysProvider>
                <FeedbackProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </FeedbackProvider>
              </ApiKeysProvider>
            </AuthProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </Auth0Provider>
    </div>
  );
}

export default App; 